/*
 * Decléor — Sticky add-to-cart bar
 * ---------------------------------------------------------------------------
 * A slim bar (image · title · price · Add-to-cart) pinned to either edge of the viewport —
 * Product → "Sticky bar position". Pinned to the TOP it has to yield to the sticky header, which is
 * what topInset() and the follow loop are for; at the BOTTOM (where this store runs it) neither is
 * involved.
 *
 * IT APPEARS ONCE THE REAL BUTTON HAS SCROLLED ABOVE THE VIEWPORT
 * ---------------------------------------------------------------------------
 * Driven by an IntersectionObserver on the real [data-add-to-cart], not by a scroll distance.
 * The bar exists to replace a button the visitor has passed, so "off screen" is not the test —
 * "above" is. On this product page the info column is long enough that the real button sits below
 * the fold even at scrollY 0, and testing only `!isIntersecting` made the bar appear on arrival,
 * offering an action nobody had scrolled to yet. A px threshold could only ever approximate any of
 * this, and got it wrong on every product whose form sits at a different height.
 * The data-offset / data-offset-mobile settings survive as the fallback for browsers without
 * IntersectionObserver and for pages where the real button cannot be found.
 *
 * IT NEVER COVERS THE HEADER
 * ---------------------------------------------------------------------------
 * See topInset() below — the header's height cannot be read from a single element in this theme.
 *
 * It REUSES the theme's own add-to-cart pipeline: the sticky button simply
 * programmatically clicks the real [data-add-to-cart] submit button inside the
 * main [data-product-form], so all variant validation, loading/success states,
 * the AJAX cart add and the cart drawer/popdown happen exactly as normal — no
 * second form, no /cart/add.js call, no edits to theme.js.
 *
 * Variant changes re-render (Alpine.morph) the product section and dispatch
 * 'theme:section:update' on window with detail.sectionId. The bar is rendered
 * OUTSIDE the morphing tree, so its nodes persist; on each update we just
 * re-read the live DOM (price, button label, sold-out) and mirror it.
 *
 * No-op on any page without a [data-sticky-atc] element.
 */
(function () {
  'use strict';

  var active = null; // controller for the bar currently on the page
  // Hoisted: sync() runs on every variant change and section re-render.
  var RE_TRAILING_ZEROS = /[.,]00(?=\D*$)/;

  function mainForm() {
    return document.querySelector('[data-product-form]');
  }
  function realButton() {
    var f = mainForm();
    return f ? f.querySelector('[data-add-to-cart]') : null;
  }

  function setup(bar) {
    if (!bar) {
      return;
    }

    var trigger = bar.querySelector('[data-sticky-atc-trigger]');
    var labelEl = bar.querySelector('[data-sticky-atc-text]');
    var priceNow = bar.querySelector('[data-sticky-price-now]');
    var priceWas = bar.querySelector('[data-sticky-price-was]');

    // Distance (px) scrolled from the top before the bar appears — separate
    // values for desktop and mobile, chosen at the 768px breakpoint (matches the CSS).
    function readOffset(attr) {
      var v = parseInt(bar.getAttribute(attr), 10);
      return isNaN(v) || v < 0 ? 0 : v;
    }
    var offsetDesktop = readOffset('data-offset');
    var offsetMobile = readOffset('data-offset-mobile');
    var desktopMq = window.matchMedia('(min-width: 768px)');
    function offsetNow() {
      return desktopMq.matches ? offsetDesktop : offsetMobile;
    }

    function visible(show) {
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      // Keep it out of the tab order / accessibility tree while off-screen.
      if ('inert' in bar) {
        bar.inert = !show;
      }
      // Only pay for the header-following rAF while the bar is actually on screen.
      if (show) {
        applyHeaderOffset(); // land on the right offset before the slide-in starts
        startFollow();
      } else {
        stopFollow();
      }
    }

    // Mirror the live (post-morph) main-form state onto the bar.
    function sync() {
      /* The BUTTON's price, not the page's. `[data-button-price]` is rendered by
       * snippets/product-buttons.liquid through `$formatCurrency(price * quantity,
       * { form: 'short' })`, so mirroring it makes the bar identical to the real button by
       * construction — no ",00" to strip, and the quantity multiplication comes along for free,
       * which is right: the sticky button submits that same quantity.
       * `[data-product-price]` stays as the fallback for a layout without the price in the button. */
      var now = document.querySelector('[data-button-price]') || document.querySelector('[data-product-price]');
      if (now && priceNow) {
        priceNow.innerHTML = now.innerHTML;
      }

      var was = document.querySelector('[data-compare-price]');
      if (priceWas) {
        if (was) {
          /* The compare price has no short-form twin in the DOM, so trim it here — a struck
           * "90,00 €" beside a live "81 €" reads as two different currencies. */
          priceWas.innerHTML = was.innerHTML.replace(RE_TRAILING_ZEROS, '');
          priceWas.hidden = false;
        } else {
          priceWas.hidden = true;
        }
      }
      if (priceNow) {
        priceNow.classList.toggle('sticky-atc__price-now--sale', !!was);
      }

      var f = mainForm();
      var txt = f && f.querySelector('[data-add-to-cart-text]');
      if (txt && labelEl) {
        labelEl.textContent = txt.textContent.trim();
      }

      var btn = realButton();
      if (btn && trigger) {
        var soldOut = !!btn.disabled;
        trigger.disabled = soldOut;
        trigger.classList.toggle('is-sold-out', soldOut);
      }
    }

    // Delegate the click to the real ATC button (bind once per bar node).
    if (!bar.dataset.stickyBound) {
      bar.dataset.stickyBound = '1';
      if (trigger) {
        trigger.addEventListener('click', function () {
          var btn = realButton();
          if (!btn || btn.disabled || btn.classList.contains('loading')) {
            return; // sold out / mid-flight — ignore (no double-submit)
          }
          btn.click();
        });
      }
    }

    active = {
      visible: visible,
      sync: sync,
      offsetNow: offsetNow,
      rewatch: function () {
        watchButton(visible, offsetNow);
      }
    };
    watchButton(visible, offsetNow);
    sync();
  }

  /* ---- What makes the bar appear ----------------------------------------
   * The real add-to-cart button leaving the viewport. Nothing else.
   *
   * `threshold: 0` + no rootMargin, then `boundingClientRect.bottom < 0` in the callback: the bar
   * takes over the instant the real button's last pixel leaves the TOP of the viewport, and not when
   * it merely sits below the fold unreached. So there is never a moment with no reachable button,
   * never two on screen at once, and nothing offered before the visitor gets there.
   *
   * Re-observed rather than re-initialised on variant change: selecting a variant morphs the
   * product form, which REPLACES the button node, and an observer still pointing at the detached
   * old node would silently stop firing — the bar would freeze in whatever state it was in.
   */
  var io = null;
  var ioTarget = null;

  function watchButton(visible, offsetNow) {
    if (io) {
      io.disconnect();
      io = null;
    }
    ioTarget = realButton();

    if (!ioTarget || typeof window.IntersectionObserver !== 'function') {
      // Fallback: the merchant's scroll-distance thresholds. Reached on a product with no form
      // (or a browser without IntersectionObserver), where there is no button to watch.
      fallbackVisible = visible;
      fallbackOffset = offsetNow;
      visible(currentY() > offsetNow());
      return;
    }
    fallbackVisible = null;
    fallbackOffset = null;

    /* ABOVE the viewport, not merely outside it.
     * `!isIntersecting` alone was wrong here, and the product page made it obvious: with the badges,
     * spec rows, CTA card, routines and reassurance blocks in the info column, the real add-to-cart
     * button now sits BELOW the fold even at the very top of the page. The observer reported it out
     * of view, so the bar appeared immediately on arrival — a sticky bar offering an action the
     * visitor has not scrolled to yet.
     * `boundingClientRect.bottom < 0` distinguishes the two cases: scrolled PAST the button (show
     * the bar) from not yet reached it (do not). rootBounds is unused so this also holds when the
     * observer fires with the page in an unusual scroll position. */
    io = new IntersectionObserver(
      function (entries) {
        var i;
        var e;
        for (i = 0; i < entries.length; i += 1) {
          e = entries[i];
          visible(!e.isIntersecting && e.boundingClientRect.bottom < 0);
        }
      },
      { threshold: 0 }
    );
    io.observe(ioTarget);
  }

  var fallbackVisible = null;
  var fallbackOffset = null;

  function currentY() {
    return window.scrollY || window.pageYOffset || 0;
  }

  /* How far down the bar must start so it never covers the header.
   *
   * DECIDED BY THE HEADER'S STATE FIRST, geometry second. An earlier version took the lowest bottom
   * edge among the header's rows in every state, on the theory that transforms make that
   * self-correcting. It is not: any measured element that does NOT ride the header's
   * `transform: translateY(-100%)` keeps reporting a positive bottom while the header is hidden, and
   * the bar stays pushed down with a gap above it — exactly the "it sits too low, and it does not
   * follow the navbar" symptom. Returning 0 unconditionally in the hidden state cannot do that.
   *
   * The three states, from the classes theme.js maintains on [data-header-wrapper]:
   *   not stuck              → in flow: clear whatever of it is still on screen
   *   stuck, not revealed    → fixed but translated away: the bar owns the top edge, inset 0
   *   stuck and revealed     → measure it; every transform is at rest, so the numbers are true
   *
   * The revealed measurement still takes the lowest bottom among the header AND its rows, because
   * this store's header is transparent: theme.css overlays `.theme__header` on a hidden
   * `.header__backfill` that reserves the height, so rows can be out of flow and the header
   * element's own box can end after the toolbar, reporting a height with the nav row missing.
   * Clamped to 40% of the viewport so a pathological measurement can never push the bar off screen.
   */
  var HEADER_WRAPPER = '[data-header-wrapper]';
  var HEADER_PARTS = '[data-header-height], [data-header-height] .header__inner';
  var headerParts = [];

  // Cached: topInset() runs on every animation frame while the bar is on screen, and the selector
  // query — not the rect read — is the expensive half.
  function refreshHeaderParts() {
    headerParts = [].slice.call(document.querySelectorAll(HEADER_PARTS));
  }

  function clampInset(px) {
    return Math.round(Math.max(0, Math.min(px, window.innerHeight * 0.4)));
  }

  function revealedHeight() {
    var max = 0;
    var i;
    var b;
    for (i = 0; i < headerParts.length; i += 1) {
      b = headerParts[i].getBoundingClientRect().bottom;
      if (b > max) {
        max = b;
      }
    }
    return max;
  }

  function topInset() {
    var wrapper = document.querySelector(HEADER_WRAPPER);
    if (!wrapper) {
      return 0;
    }
    var c = wrapper.classList;
    if (!c.contains('js__header__stuck')) {
      return clampInset(wrapper.getBoundingClientRect().bottom);
    }
    if (c.contains('js__header__stuck--animated') && !c.contains('js__header__stuck--trigger-animation')) {
      return 0;
    }
    return clampInset(revealedHeight());
  }

  function applyHeaderOffset() {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar || bar.getAttribute('data-position') === 'bottom') {
      return; // a bottom-pinned bar never collides with the header
    }
    var top = topInset() + 'px';
    // Compare before writing: this runs on every animation frame while the bar is on screen, and
    // an unchanged inline-style write would invalidate style for nothing.
    if (bar.style.getPropertyValue('--dc-atc-top') !== top) {
      bar.style.setProperty('--dc-atc-top', top);
    }
  }

  /* Follow the header for as long as the bar is on screen.
   * The header animates for 0.2s AFTER the last scroll event, so anything driven purely by
   * scroll events freezes the bar mid-step when the visitor lifts their finger. One rect read
   * per frame (and a write only when the value actually changes) buys exact lockstep instead. */
  var followId = null;
  function follow() {
    applyHeaderOffset();
    followId = requestAnimationFrame(follow);
  }
  function startFollow() {
    if (followId === null) {
      follow();
    }
  }
  function stopFollow() {
    if (followId !== null) {
      cancelAnimationFrame(followId);
      followId = null;
    }
  }

  function init() {
    refreshHeaderParts();
    setup(document.querySelector('[data-sticky-atc]'));
  }

  init();

  /* Scroll only drives the FALLBACK path now — when there is no real button to observe (a product
   * with no form) or no IntersectionObserver. In the normal case the observer owns visibility and
   * the rAF loop owns the offset, so there is nothing for a scroll listener to do. */
  var ticking = false;
  function onScroll() {
    if (ticking || !fallbackVisible) {
      return;
    }
    ticking = true;
    requestAnimationFrame(function () {
      fallbackVisible(currentY() > fallbackOffset());
      ticking = false;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  // Resize changes the fallback's breakpoint-dependent threshold. The offset needs no handler:
  // while the bar is on screen the follow loop is already re-measuring every frame, and while it
  // is off screen the offset is recomputed on the way in.
  window.addEventListener(
    'resize',
    function () {
      refreshHeaderParts();
      onScroll();
    },
    { passive: true }
  );

  // Variant change → section morph → re-read the live DOM.
  window.addEventListener('theme:section:update', function (e) {
    if (!active) {
      return;
    }
    var bar = document.querySelector('[data-sticky-atc]');
    if (bar && e && e.detail && String(e.detail.sectionId) !== String(bar.dataset.sectionId)) {
      return;
    }
    active.sync();
    // The morph replaced the form, so the observed button node is now detached and its observer
    // would never fire again. Re-point it at the new one.
    active.rewatch();
  });

  // Theme editor re-renders the whole section. Use Shopify's native event, which fires on DOCUMENT once
  // the replacement HTML has landed. The theme's own 'theme:section:load' is dispatched on WINDOW (and
  // before the section fetch), so this listener never fired — same bug already fixed in gold-flourish.js
  // and quantity-number-flow.js. Clearing `active` is required: if the reloaded section has no
  // [data-sticky-atc], setup() early-returns and `active` would keep writing classes onto a detached node.
  document.addEventListener('shopify:section:load', function () {
    active = null;
    // Tear down what points at the outgoing DOM before rebuilding: an observer left on the old
    // button and a rAF loop writing to a detached bar would both survive the re-render otherwise.
    stopFollow();
    if (io) {
      io.disconnect();
      io = null;
    }
    fallbackVisible = null;
    fallbackOffset = null;
    init();
  });
})();
